import { Cell, Fill, Row, Worksheet } from 'exceljs';

import { ColumnRequirement } from 'support/types/webediTable';

import { ExcelColumnDefinition } from '../types';

// Not `as const`: the argb values feed sibling STYLES entries (required/conditional/optional) that
// must share one type per band so they can live in a `Record<ColumnRequirement, ...>` lookup below —
// literal argb types would make each entry structurally distinct and fail that assignment.
const COLORS: Record<string, string> = {
  WHITE: 'FFFFFFFF',
  BLACK: 'FF000000',
  GRAY: 'FF808080',
  LIGHT_GRAY: 'FFD3D3D3',
  REQUIRED_HEADER_DARK_RED: 'FF5B0F00',
  REQUIRED_SUBHEADER_RED: 'FF8A4A2A',
  REQUIRED_DESCRIPTION_LIGHT_RED: 'FFF5CBCC',
  CONDITIONAL_HEADER_DARK_ORANGE: 'FF783F04',
  CONDITIONAL_SUBHEADER_ORANGE: 'FFB45F06',
  CONDITIONAL_DESCRIPTION_LIGHT_ORANGE: 'FFFCE5CD',
  OPTIONAL_HEADER_DARK_BLUE: 'FF1C4587',
  OPTIONAL_SUBHEADER_BLUE: 'FF4D6AA6',
  OPTIONAL_DESCRIPTION_LIGHT_BLUE: 'FFD0E2F3',
};

const EXCEL_LAYOUT_CONSTANTS = {
  MIN_COLUMN_WIDTH: 15,
  HEADER_PADDING: 2,
  DESCRIPTION_WIDTH_DIVISOR: 3,
  CHARS_PER_LINE_FACTOR: 1.5,
  LINE_HEIGHT_PIXELS: 15,
} as const;

const STYLES = {
  instructionRow: {
    font: { bold: true, color: { argb: COLORS.BLACK } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.LIGHT_GRAY } },
    height: 75,
  },
  instructionCell: {
    alignment: { vertical: 'middle', horizontal: 'left', wrapText: true },
  },
  requiredHeaderCell: {
    font: { bold: true, color: { argb: COLORS.WHITE } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.REQUIRED_HEADER_DARK_RED } },
    alignment: { vertical: 'middle' },
  },
  conditionalHeaderCell: {
    font: { bold: true, color: { argb: COLORS.WHITE } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.CONDITIONAL_HEADER_DARK_ORANGE } },
    alignment: { vertical: 'middle' },
  },
  optionalHeaderCell: {
    font: { bold: true, color: { argb: COLORS.WHITE } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.OPTIONAL_HEADER_DARK_BLUE } },
    alignment: { vertical: 'middle' },
  },
  requiredSubHeaderCell: {
    font: { bold: true, color: { argb: COLORS.WHITE } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.REQUIRED_SUBHEADER_RED } },
    alignment: { vertical: 'middle' },
  },
  conditionalSubHeaderCell: {
    font: { bold: true, color: { argb: COLORS.WHITE } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.CONDITIONAL_SUBHEADER_ORANGE } },
    alignment: { vertical: 'middle' },
  },
  optionalSubHeaderCell: {
    font: { bold: true, color: { argb: COLORS.WHITE } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.OPTIONAL_SUBHEADER_BLUE } },
    alignment: { vertical: 'middle' },
  },
  requiredDescriptionCell: {
    font: { italic: false, size: 9 },
    alignment: { wrapText: true, vertical: 'top' },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.REQUIRED_DESCRIPTION_LIGHT_RED } },
  },
  conditionalDescriptionCell: {
    font: { italic: false, size: 9 },
    alignment: { wrapText: true, vertical: 'top' },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.CONDITIONAL_DESCRIPTION_LIGHT_ORANGE } },
  },
  optionalDescriptionCell: {
    font: { italic: false, size: 9 },
    alignment: { wrapText: true, vertical: 'top' },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.OPTIONAL_DESCRIPTION_LIGHT_BLUE } },
  },
};

const HEADER_STYLES: Record<ColumnRequirement, typeof STYLES.requiredHeaderCell> = {
  required: STYLES.requiredHeaderCell,
  conditional: STYLES.conditionalHeaderCell,
  optional: STYLES.optionalHeaderCell,
};

const SUB_HEADER_STYLES: Record<ColumnRequirement, typeof STYLES.requiredSubHeaderCell> = {
  required: STYLES.requiredSubHeaderCell,
  conditional: STYLES.conditionalSubHeaderCell,
  optional: STYLES.optionalSubHeaderCell,
};

const DESCRIPTION_STYLES: Record<ColumnRequirement, typeof STYLES.requiredDescriptionCell> = {
  required: STYLES.requiredDescriptionCell,
  conditional: STYLES.conditionalDescriptionCell,
  optional: STYLES.optionalDescriptionCell,
};

/**
 * Adds an instructions row to the Excel worksheet
 * @param worksheet The Excel worksheet to modify
 * @param columns Column definitions
 * @param instructionText The instruction text to display (optional)
 * @returns The row number where the instructions were added, or 0 if no instructions
 */
export const addInstructionsRow = (
  worksheet: Worksheet,
  columns: Array<ExcelColumnDefinition>,
  instructionText?: string,
): number => {
  if (!instructionText) {
    return 0;
  }

  const instructionRow = worksheet.addRow([instructionText]);
  applyRowStyles(instructionRow, STYLES.instructionRow);

  // Get the last column letter properly
  const lastColumnIndex = columns.length;
  const lastColumn = worksheet.getColumn(lastColumnIndex);
  const lastColumnLetter = lastColumn.letter;

  // Merge cells from A1 to the last column
  worksheet.mergeCells(`A1:${lastColumnLetter}1`);

  const instructionCell = worksheet.getCell('A1');
  applyCellStyles(instructionCell, STYLES.instructionCell);

  return 1;
};

/**
 * Adds header rows with appropriate styling to the Excel worksheet
 * @param worksheet The Excel worksheet to modify
 * @param columns Column definitions to create headers for
 * @param startRowIndex The row index to use for headers
 */
export const addHeadersRows = (
  worksheet: Worksheet,
  columns: Array<ExcelColumnDefinition>,
  startRowIndex = 1,
): number => {
  let currentColumnIndex = 1;
  let hasSubHeaders = false;

  columns.forEach((columnDefinition) => {
    const column = worksheet.getColumn(currentColumnIndex);
    column.width = Math.max(
      EXCEL_LAYOUT_CONSTANTS.MIN_COLUMN_WIDTH,
      columnDefinition.header.length + EXCEL_LAYOUT_CONSTANTS.HEADER_PADDING,
    );

    const headerRow = worksheet.getRow(startRowIndex);
    const subColumnsLength = columnDefinition.columns?.length || 0;

    if (subColumnsLength > 0) {
      hasSubHeaders = true;
      const lastColumnIndex = currentColumnIndex + subColumnsLength - 1;
      const lastColumn = worksheet.getColumn(lastColumnIndex);
      worksheet.mergeCells(`${column.letter}${startRowIndex}:${lastColumn.letter}${startRowIndex}`);
    }

    const headerCell = headerRow.getCell(currentColumnIndex);
    headerCell.value = columnDefinition.header;
    headerCell.protection = { locked: true };

    applyCellStyles(headerCell, HEADER_STYLES[columnDefinition.requirement]);

    currentColumnIndex += subColumnsLength || 1;
  });

  if (!hasSubHeaders) {
    return 1;
  }

  currentColumnIndex = 1;
  const subHeaderRow = worksheet.getRow(startRowIndex + 1);

  columns.forEach((columnDefinition) => {
    const subColumns = columnDefinition.columns || [];

    if (subColumns.length === 0) {
      const column = worksheet.getColumn(currentColumnIndex);
      worksheet.mergeCells(`${column.letter}${startRowIndex}:${column.letter}${startRowIndex + 1}`);
      currentColumnIndex += 1;
      return;
    }

    subColumns.forEach((subColumn) => {
      const column = worksheet.getColumn(currentColumnIndex);
      column.width = Math.max(
        EXCEL_LAYOUT_CONSTANTS.MIN_COLUMN_WIDTH,
        subColumn.header.length + EXCEL_LAYOUT_CONSTANTS.HEADER_PADDING,
      );

      const subHeaderCell = subHeaderRow.getCell(currentColumnIndex);
      subHeaderCell.value = subColumn.header;
      subHeaderCell.protection = { locked: true };

      applyCellStyles(subHeaderCell, SUB_HEADER_STYLES[subColumn.requirement]);

      currentColumnIndex += 1;
    });
  });

  return 2;
};

/**
 * Adds description rows to the Excel worksheet
 * @param worksheet The Excel worksheet to modify
 * @param columns Column definitions with descriptions
 * @param startRowIndex The row index to use for descriptions
 */
export const addDescriptionRow = (
  worksheet: Worksheet,
  columns: Array<ExcelColumnDefinition>,
  startRowIndex = 2,
): void => {
  columns
    .flatMap((column) => (column.columns && column.columns.length > 0 ? column.columns : [column])) // Flatten one level of embedded table columns
    .forEach((columnDefinition, index) => {
      const column = worksheet.getColumn(index + 1);
      const descriptionRow = worksheet.getRow(startRowIndex);
      const descriptionCell = descriptionRow.getCell(index + 1);
      descriptionCell.value = columnDefinition.description || '';

      applyCellStyles(descriptionCell, DESCRIPTION_STYLES[columnDefinition.requirement]);

      // Adjust column width to fit description if needed
      column.width = Math.max(
        column.width || 0,
        descriptionCell.value.length / EXCEL_LAYOUT_CONSTANTS.DESCRIPTION_WIDTH_DIVISOR,
      );

      // Make the description row taller to accommodate text
      // Set row height dynamically based on description length
      const estCharactersPerLine = column.width * EXCEL_LAYOUT_CONSTANTS.CHARS_PER_LINE_FACTOR;
      const estimatedLines = descriptionCell.value
        ? Math.ceil(String(descriptionCell.value).length / estCharactersPerLine)
        : 1;
      const lineHeight = EXCEL_LAYOUT_CONSTANTS.LINE_HEIGHT_PIXELS;
      const rowHeight = Math.max(descriptionRow.height, estimatedLines * lineHeight);

      // Update the row height to fit the largest description
      descriptionRow.height = rowHeight;
    });
};

/**
 * Adds a hidden row with column keys to the Excel worksheet
 * For embedded table columns, combines parent and child keys as "parentKey.*.subKey"
 * @param worksheet The Excel worksheet to modify
 * @param columns Column definitions with keys
 * @param startRowIndex The row index to use for keys
 */
export const addKeysRow = (worksheet: Worksheet, columns: Array<ExcelColumnDefinition>, startRowIndex = 2): void => {
  const keyRow = worksheet.getRow(startRowIndex);

  // Hide the key row
  keyRow.hidden = true;
  // Lock to prevent user modifications
  keyRow.protection = { locked: true };

  let cellIndex = 1;

  columns.forEach((column) => {
    if (column.columns && column.columns.length > 0) {
      column.columns.forEach((subColumn) => {
        const keyCell = keyRow.getCell(cellIndex);
        keyCell.value = subColumn.key;

        applyCellStyles(keyCell, {
          font: { size: 9, bold: false, color: { argb: COLORS.GRAY } },
          alignment: { vertical: 'middle', horizontal: 'left' },
        });

        keyCell.protection = { locked: true };
        cellIndex += 1;
      });
    } else {
      const keyCell = keyRow.getCell(cellIndex);
      keyCell.value = column.key;

      applyCellStyles(keyCell, {
        font: { size: 9, bold: false, color: { argb: COLORS.GRAY } },
        alignment: { vertical: 'middle', horizontal: 'left' },
      });

      keyCell.protection = { locked: true };
      cellIndex += 1;
    }
  });
};

/**
 * Configure worksheet view settings (freezing header rows, etc.)
 * @param worksheet The Excel worksheet to modify
 * @param freezeHeaderRows Whether to freeze header rows
 * @param headerRowCount Number of header rows to freeze
 */

export const configureWorksheetView = (worksheet: Worksheet, freezeHeaderRows = true, headerRowCount: number): void => {
  if (freezeHeaderRows) {
    worksheet.views = [
      {
        state: 'frozen',
        xSplit: 0,
        ySplit: headerRowCount - 1,
        topLeftCell: `A${headerRowCount}`,
        activeCell: `A${headerRowCount}`,
      },
    ];
  }
};

/**
 * Apply styles to a row in Excel
 */
const applyRowStyles = (row: Row, styles: typeof STYLES.instructionRow): void => {
  if ('font' in styles) {
    row.font = styles.font;
  }
  if ('fill' in styles) {
    row.fill = styles.fill as Fill;
  }
  if ('height' in styles) {
    row.height = styles.height;
  }
};

/**
 * Apply styles to a cell in Excel
 */
const applyCellStyles = (cell: Cell, styles: Record<string, unknown>): void => {
  if ('font' in styles) {
    cell.font = styles.font as Cell['font'];
  }
  if ('fill' in styles) {
    cell.fill = styles.fill as Cell['fill'];
  }
  if ('alignment' in styles) {
    cell.alignment = styles.alignment as Cell['alignment'];
  }
};
